<template>
  <div>
    <v-data-table :headers="headers" :items="managedUsers" style="width: 100%;" :search="search" :loading="loading">
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-spacer></v-spacer>
          <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          :label="$t('components.RoleManager.search')"
          single-line
          hide-details
          ></v-text-field>
        </v-toolbar>
      </template>
      <template v-slot:item.roles="{ item }">
        <v-btn icon @click="showDialog(item)">
          <v-icon small>mdi-account-supervisor</v-icon>
        </v-btn>
      </template>
    </v-data-table>
    <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition" persistent>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click.stop="dialog = false; selectedUser = {}">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t('components.RoleManager.rolesForUser') }} {{ selectedUser.email }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark text @click.stop="saveDialog = true">{{ $t('components.RoleManager.save') }}</v-btn>
            <v-dialog v-model="saveDialog" max-width="400">
              <v-card>
                <v-card-title class="headline">{{ $t('components.RoleManager.warning') }}</v-card-title>
                <v-card-text>
                  {{ $t('components.RoleManager.cannotBeUndone') }}
                </v-card-text>
                <v-card-actions v-if="!processing">
                  <v-spacer></v-spacer>
                  <v-btn color="gray darken-1" text @click="saveDialog = false">{{ $t('components.RoleManager.abort') }}</v-btn>
                  <v-btn color="red darken-1" text @click="updateUserRoles()">{{ $t('components.RoleManager.confirm') }}</v-btn>
                </v-card-actions>
                <v-card-actions v-else>
                  <v-spacer></v-spacer>
                  {{ $t('components.RoleManager.processing') }}
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>
        <v-row>
          <v-col cols="12">
            <v-data-table v-model="selectedRoles" :headers="roleHeaders" :items="roles" class="elevation-1" style="width: 95%;" show-select item-key="name" disable-pagination hide-default-footer :search="searchRoles">
              <template v-slot:top>
                <v-toolbar flat color="white">
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="searchRoles"
                    append-icon="mdi-magnify"
                    :label="$t('components.RoleManager.search')"
                    single-line
                    hide-details
                  ></v-text-field>
                </v-toolbar>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>

    <!-- Alert Snackbar -->
    <v-snackbar
      v-model="alert.show"
      :color="alert.type"
      :timeout="5000"
      bottom
      right
    >
      {{ alert.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="alert.show = false"
        >
          {{ $t('components.RoleManager.close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
export default {
  data () {
    return {
      search: '',
      searchRoles: '',
      loading: true,
      processing: false,
      selectedUser: {},
      dialog: false,
      saveDialog: false,
      selectedRoles: [],
      alert: {
        show: false,
        type: 'success',
        message: ''
      }
    }
  },
  methods: {
    ...mapActions('manager', [
      'setUserRolesWithAudit',
      'getUserRoles'
    ]),
    showDialog: function (item) {
      this.selectedUser = item
      this.dialog = true
    },
    updateUserRoles: async function () {
      this.processing = true
      try {
        const result = await this.setUserRolesWithAudit({
          userId: this.selectedUser.id,
          selectedRoles: this.selectedRoles.map(role => role.name)
        })

        if (result && result.success) {
          this.showAlert({
            type: 'success',
            message: result.message || this.$t('components.RoleManager.rolesUpdated')
          })
        } else {
          this.showAlert({
            type: 'error',
            message: this.$t('components.RoleManager.failedToUpdateRoles')
          })
        }
      } catch (error) {
        console.error('Error updating roles:', error)
        this.showAlert({
          type: 'error',
          message: this.$t('components.RoleManager.errorUpdatingRoles') + ' ' + (error.message || 'Unknown error')
        })
      } finally {
        this.saveDialog = false
        this.dialog = false
        this.processing = false
      }
    },
    showAlert (alertData) {
      this.alert = {
        show: true,
        type: alertData.type || 'info',
        message: alertData.message || 'Action completed'
      }
      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('manager', [
      'managedUsers'
    ]),
    headers () {
      return [
        { text: this.$t('components.RoleManager.name'), value: 'firstName' },
        { text: this.$t('components.RoleManager.lastname'), value: 'lastName' },
        { text: this.$t('components.RoleManager.email'), value: 'email' },
        { text: this.$t('components.RoleManager.roles'), value: 'roles' }
      ]
    },
    roleHeaders () {
      return [
        { text: this.$t('components.RoleManager.role'), value: 'name' },
        { text: this.$t('components.RoleManager.description'), value: 'description' }
      ]
    },
    roles () {
      return [
        { name: 'model_access_manager', description: this.$t('components.RoleManager.modelAccessManager') },
        { name: 'ekrm_manager', description: this.$t('components.RoleManager.ekrmManager') },
        { name: 'data_uploader', description: this.$t('components.RoleManager.dataUploader') },
        { name: 'only_show_model_report', description: this.$t('components.RoleManager.onlyShowModelReport') },
        { name: 'external_user', description: this.$t('components.RoleManager.externalUser') },
        { name: 'diy_modeler', description: this.$t('components.RoleManager.diyModeler') }
      ]
    }
  },
  watch: {
    'selectedUser': function (user) {
      this.getUserRoles({ userId: user.id }).then(userRoles => {
        this.selectedRoles = this.roles.filter(role => userRoles.includes(role.name))
      })
    },
    'managedUsers': function (users) {
      // Stop loading spinner once managedUsers data is available
      if (users && users.length >= 0) {
        this.loading = false
      }
    }
  },
  created: function () {
    // If managedUsers are already loaded when component is created, stop loading immediately
    if (this.managedUsers && this.managedUsers.length >= 0) {
      this.loading = false
    }
  }
}
</script>
